import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronDown } from 'lucide-react';
import { useLocaleContext } from '../../context/LocaleContext';
import { locales, urlMappings } from '../../utils/hreflang';
import {
  toolsSections,
  resourcesNavItems,
  apiNavItems,
} from '../../utils/navigationData';
import { getTranslation } from '../../utils/translations';
import * as Icons from 'lucide-react';

// Custom hook for robust body scroll locking with cleanup guarantees
const useBodyScrollLock = (isLocked: boolean) => {
  const lockCountRef = useRef(0);
  const originalStyleRef = useRef<string>('');

  useEffect(() => {
    if (isLocked) {
      if (lockCountRef.current === 0) {
        originalStyleRef.current = document.body.style.overflow;
      }
      lockCountRef.current++;
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (isLocked && lockCountRef.current > 0) {
        lockCountRef.current--;
        if (lockCountRef.current === 0) {
          document.body.style.overflow = originalStyleRef.current;
        }
      }
    };
  }, [isLocked]);

  useEffect(() => {
    const cleanup = () => {
      if (lockCountRef.current > 0) {
        document.body.style.overflow = originalStyleRef.current;
        lockCountRef.current = 0;
      }
    };

    window.addEventListener('beforeunload', cleanup);

    return () => {
      cleanup();
      window.removeEventListener('beforeunload', cleanup);
    };
  }, []);
};

interface MobileMenuPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenuPanel: React.FC<MobileMenuPanelProps> = ({ isOpen, onClose }) => {
  const { locale } = useLocaleContext();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Translation key mappings
  const toolsTranslationKeys: Record<string, { title: string; desc: string }> = {
    'ChatGPT Watermarks': { title: 'nav.chatgptWatermarks', desc: 'nav.chatgptWatermarksDesc' },
    'Gemini Watermarks': { title: 'nav.geminiWatermarks', desc: 'nav.geminiWatermarksDesc' },
    'Claude Watermarks': { title: 'nav.claudeWatermarks', desc: 'nav.claudeWatermarksDesc' },
    'Gemini Images': { title: 'nav.geminiImages', desc: 'nav.geminiImagesDesc' },
    'DOCX Cleaner': { title: 'nav.docxCleaner', desc: 'nav.docxCleanerDesc' },
    'PDF Cleaner': { title: 'nav.pdfCleaner', desc: 'nav.pdfCleanerDesc' },
    'Pages Cleaner': { title: 'nav.pagesCleaner', desc: 'nav.pagesCleanerDesc' },
    'Chrome Extension': { title: 'nav.chromeExtension', desc: 'nav.chromeExtensionDesc' },
    'For Students': { title: 'nav.forStudents', desc: 'nav.forStudentsDesc' },
  };

  const sectionTranslationKeys: Record<string, string> = {
    'TEXT WATERMARK REMOVAL': 'nav.textWatermarkRemoval',
    'IMAGE WATERMARK REMOVAL': 'nav.imageWatermarkRemoval',
    'DOCUMENT TOOLS': 'nav.documentTools',
    'EXTENSIONS': 'nav.extensions',
  };

  const resourcesTranslationKeys: Record<string, { title: string; desc: string }> = {
    'Knowledge Hub': { title: 'nav.knowledgeHub', desc: 'nav.knowledgeHubDesc' },
    'Blog': { title: 'nav.blog', desc: 'nav.blogDesc' },
    'FAQ': { title: 'nav.faq', desc: 'nav.faqDesc' },
    'About': { title: 'nav.about', desc: 'nav.aboutDesc' },
  };

  const apiTranslationKeys: Record<string, { title: string; desc: string }> = {
    'API Overview': { title: 'nav.apiOverview', desc: 'nav.apiOverviewDesc' },
    'Developer Docs': { title: 'nav.developerDocs', desc: 'nav.developerDocsDesc' },
    'For Writing Tools': { title: 'nav.forWritingTools', desc: 'nav.forWritingToolsDesc' },
    'For Chatbots': { title: 'nav.forChatbots', desc: 'nav.forChatbotsDesc' },
    'For Content Platforms': { title: 'nav.forContentPlatforms', desc: 'nav.forContentPlatformsDesc' },
  };

  // Prevent body scroll when menu is open
  useBodyScrollLock(isOpen);

  // Get locale-aware path
  const getLocalePath = (path: string) => {
    const prefix = locales[locale] || '';
    const mappings = urlMappings[path];
    if (mappings && mappings[locale]) {
      return `${prefix}${mappings[locale]}`;
    }
    return `${prefix}${path}`;
  };

  // Get icon component dynamically
  const getIcon = (iconName: string, size: number = 18) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent ? <IconComponent size={size} aria-hidden="true" /> : null;
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSection(expandedSection === sectionName ? null : sectionName);
  };

  const handleLinkClick = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-out Panel */}
      <div
        className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-neutral-900 shadow-2xl z-50 overflow-y-auto"
        style={{
          animation: 'slideInFromRight 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 px-4 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {getTranslation(locale, 'nav.menu')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            aria-label="Close menu"
          >
            <X size={20} className="text-neutral-600 dark:text-neutral-400" />
          </button>
        </div>

        {/* Menu Content */}
        <div className="px-4 py-4 space-y-4">
          {/* Tools Section */}
          <div>
            <button
              onClick={() => toggleSection('tools')}
              className="w-full flex items-center justify-between py-2 text-left"
              aria-expanded={expandedSection === 'tools'}
            >
              <span className="font-semibold text-neutral-900 dark:text-white">
                {getTranslation(locale, 'nav.tools')}
              </span>
              <ChevronDown
                size={20}
                className={`text-neutral-600 dark:text-neutral-400 transition-transform ${
                  expandedSection === 'tools' ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSection === 'tools' && (
              <div className="mt-2 space-y-3">
                {toolsSections.map((section, sectionIndex) => (
                  <div key={sectionIndex}>
                    <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider px-2 mb-1">
                      {getTranslation(locale, sectionTranslationKeys[section.title] || '')}
                    </h3>
                    <div className="space-y-1">
                      {section.items.map((item, itemIndex) => {
                        const isExternal = item.path.startsWith('http');
                        const translationKey = toolsTranslationKeys[item.title];
                        const title = translationKey ? getTranslation(locale, translationKey.title) : item.title;

                        if (isExternal) {
                          return (
                            <a
                              key={itemIndex}
                              href={item.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={handleLinkClick}
                              className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                            >
                              <div className="text-neutral-600 dark:text-neutral-400">
                                {getIcon(item.icon || 'Circle', 16)}
                              </div>
                              <span className="text-sm text-neutral-700 dark:text-neutral-300">
                                {title}
                              </span>
                            </a>
                          );
                        }

                        return (
                          <Link
                            key={itemIndex}
                            to={getLocalePath(item.path)}
                            onClick={handleLinkClick}
                            className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                          >
                            <div className="text-neutral-600 dark:text-neutral-400">
                              {getIcon(item.icon || 'Circle', 16)}
                            </div>
                            <span className="text-sm text-neutral-700 dark:text-neutral-300">
                              {title}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resources Section */}
          <div>
            <button
              onClick={() => toggleSection('resources')}
              className="w-full flex items-center justify-between py-2 text-left"
              aria-expanded={expandedSection === 'resources'}
            >
              <span className="font-semibold text-neutral-900 dark:text-white">
                {getTranslation(locale, 'nav.resources')}
              </span>
              <ChevronDown
                size={20}
                className={`text-neutral-600 dark:text-neutral-400 transition-transform ${
                  expandedSection === 'resources' ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSection === 'resources' && (
              <div className="mt-2 space-y-1">
                {resourcesNavItems.map((item, index) => {
                  const translationKey = resourcesTranslationKeys[item.title];
                  const title = translationKey ? getTranslation(locale, translationKey.title) : item.title;

                  return (
                    <Link
                      key={index}
                      to={getLocalePath(item.path)}
                      onClick={handleLinkClick}
                      className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                    >
                      <div className="text-neutral-600 dark:text-neutral-400">
                        {getIcon(item.icon || 'Circle', 16)}
                      </div>
                      <span className="text-sm text-neutral-700 dark:text-neutral-300">
                        {title}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* API Section */}
          <div>
            <button
              onClick={() => toggleSection('api')}
              className="w-full flex items-center justify-between py-2 text-left"
              aria-expanded={expandedSection === 'api'}
            >
              <span className="font-semibold text-neutral-900 dark:text-white">
                {getTranslation(locale, 'nav.api')}
              </span>
              <ChevronDown
                size={20}
                className={`text-neutral-600 dark:text-neutral-400 transition-transform ${
                  expandedSection === 'api' ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSection === 'api' && (
              <div className="mt-2 space-y-1">
                {apiNavItems.map((item, index) => {
                  const translationKey = apiTranslationKeys[item.title];
                  const title = translationKey ? getTranslation(locale, translationKey.title) : item.title;

                  return (
                    <Link
                      key={index}
                      to={getLocalePath(item.path)}
                      onClick={handleLinkClick}
                      className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                    >
                      <div className="text-neutral-600 dark:text-neutral-400">
                        {getIcon(item.icon || 'Circle', 16)}
                      </div>
                      <span className="text-sm text-neutral-700 dark:text-neutral-300">
                        {title}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      <style>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default React.memo(MobileMenuPanel);
